import React from "react";

import {BANNER_TYPE_ERROR, BANNER_TYPE_SUCCESS, BANNER_TYPE_WARNING} from "../../util/ConfigConstants";
import { Header, Icon, Popover, SpaceBetween } from "@amzn/awsui-components-react-v3";
import BlockedDeliveriesSummary from "./common/BlockedDeliveriesSummary";
import PlannedDeliveriesResult from "./common/PlannedDeliveriesResult";
import HubSummary from "./lastMileHubView/interfaces/HubSummary";

export const ONE_DAY_MILLIS_EPOCH: number = 86400000;

export const COMMA_DELIMITER = ', ';

export const MAX_SELECT_ALLOWED = 3;

export const PLANNER_AUDIT_HISTORY_TIME_ZONE = 'PST8PDT';

export const NUMBER_OF_DAYS_PLANNED_DELIVERIES = 4;

export const INCREMENT_DATE_BY_ONE_EPOCH_MILLI = 24 * 60 * 60 * 1000;

export enum REFERENCE_ID_ENUM {
    TDD = "tdd",
    BRICK = "brickHostname",
    EDD_SOURCE = "estimatedDeliveryDateSource",
    PROMISE_DATE = "promiseDate",
    IPN = "ipn",
    WEIGHT = "weight",
    BRICK_HANDOFF_DATE = "brickHandoffDate",
    POSITION_HANDOFF_DATE = "positionHandoffDate",
    POSITION_READY_STATUS = "positionReadyStatus",
    BLOCKER = "blocker",
    BLOCKING_REASON = "blockingReason",
    HUB_RECEIVED_AT = "hubReceivedAt",
    DWELL_TIME = "dwellTime",
    DESTINATION_HUB_ETA = "destinationHubEta",
    PRESENT_AT_DEST_HUB = "presentAtDestHub",
    INTERNAL_RACK_TYPE = "internalRackType",
    EARLIEST_DELIVERY_DATE = "earliestDeliveryDate",
    TDD_SOURCE = "tddSource",
    PFHO = "PFHO",
    RTS_TIME = "rtsTime",
    SHIPMENT_STATUS = "status",
    DESTINATION_HUB_ETA_SOURCE = "destinationHubEtaSource",
    ROOS_PRIORITY = "ROOSPriority",
}

export enum TDD_SOURCE {
    OVERRIDE = "Override",
    CLOUDFORGE = "CloudForge",
}

export enum EDD_SOURCE {
    CARRIER = "CARRIER",
    PLANNED_TDD = "PLANNED_TDD",
    USER_MANDATED = "USER_MANDATED",
}

export enum TDD_STATUS {
    LOCKED = "LOCKED",
    UNLOCKED = "UNLOCKED"
}

export enum BLOCKER {
    POSITION = "POSITION",
    THROUGHPUT = "THROUGHPUT"
}

export enum PDD_SOURCE_DISPLAY_TEXT {
    CARRIER = "Carrier",
    USER_MANDATED_TDD = "TDD - User mandated",
    SYSTEM_CALCULATED_TDD = "TDD - System calculated",
    DEFAULT = "System Estimate"
}

export const PDD_SOURCE_DESCRIPTION = {
    CARRIER : {
        label: "Carrier",
        description: "Planned delivery date based on carrier-provided estimate."
    },
    USER_MANDATED_TDD : {
        label: "TDD - User mandated",
        description: "Target delivery date manually overridden by a user."
    },
    SYSTEM_CALCULATED_TDD : {
        label: "TDD - System calculated",
        description: "Target delivery date calculated automatically by the system."
    },
    DEFAULT : {
        label: "System Estimate",
        description: "Planned delivery date based upon system estimate."
    }
}

export enum TDD_SOURCE_DISPLAY_NAME {
    OVERRIDE = "User mandated",
    CLOUDFORGE = "System calculated",
    DEFAULT = "-"
}

export enum PLANNING_TYPE {
    PLANNED = "PLANNED",
    ESTIMATED = "ESTIMATED"
}

export enum BLOCKER_BASED_SUGGESTED_ACTIONS {
    REMOVE_OVERRIDE = "Reset racks",
    REMOVE_POSITION_BLOCKER = "Remove Position Blocker",
    INCREASE_THROUGHPUT = "Increase Throughput",
    HOLIDAY_BLOCKER = "NA - Delay due to holiday",
    EXPEDITE = "Expedite Shipment",
    CARRIER_EXPEDITED = "NA - Carrier expedited the shipment",
    CARRIER_DELAYED = "Carrier delayed the shipment, communicate offline to expedite",
    NO_ACTION = "NA - Shipment already locked"
}

export const EMPTY_BLOCKED_DELIVERIES_SUMMARY: BlockedDeliveriesSummary = {
    throughputBlockedRacksCount: 0,
    redPositionBlockedRacksCount: 0,
    yellowPositionBlockedRacksCount: 0
}

export const EMPTY_PLANNED_DELIVERY_RESULT: PlannedDeliveriesResult = {
    deliveries: [],
    aggregateDeliveryResults: [],
    weeklyDeliverySummaryResults: [],
    positionBlockedSummaryResults: {
        total: 0,
        buckets: []
    },
    throughputBlockedSummaryResults: {
        total: 0,
        buckets: []
    }
}

export const EMPTY_HUB_SUMMARY: HubSummary = {
    totalCount: 0,
    outgoingRacksCount: 0,
    approachingPromiseDateRacksCount: 0,
    dwellTimeStatistics: { p90DwellTime: -1, dimensions: new Map() },
    position: { total: 0, buckets: [] },
    throughput: { total: 0, buckets: [] },
    missedTdd: 0
};

export const LMDP_EIGHT_WEEK_FORECAST_FORECAST_PAGE = {
    BREADCRUMB_ITEMS : [
        {
            "text": "LMDP",
            "href": "/lmdp"
        },
        {
            "text": "Delivery Forecast",
            "href": "#"
        }
    ],
    PAGE_HEADER: "Delivery Forecast",

    RESULT : {
        RACKS_PER_WEEK_TABLE: {
            TABLE_HEADER: <Header variant="h3">
                    <SpaceBetween size={"s"} direction="horizontal">
                        <>Forecast (Unconstraint)</>
                        <Popover
                        dismissButton={false}
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            "Count of racks that are planned to be delivered, without any throughput constraints."
                        }
                        >
                        <Icon name="status-info" variant="link" />
                        </Popover>
                    </SpaceBetween>
                </Header>,
            SIM_TABLE_HEADER: <Header variant="h3">
                    <SpaceBetween size={"s"} direction="horizontal">
                        <>Forecast (Constraint)</>
                        <Popover
                        dismissButton={false}
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            "Count of racks that are planned to be delivered, with current set of throughput constratint applied."
                        }
                        >
                        <Icon name="status-info" variant="link" />
                        </Popover>
                    </SpaceBetween>
                </Header>,
            SUPER_AZ_NAME: "SuperAz",
            RACK_TYPE_NAME: "Rack Type",
            CLUSTER_NAME: "Cluster",
            WEEK: "Week"
        },
        RAW_DATA_TABLE: {
            TABLE_HEADER: "Raw Data",
            SITE_NAME: "Site",
            ETA: "Expected Time of Arrival",
            DEMAND_ID: "Demand Id"
        }
    }
}
export const PLANNED_DELIVERIES_PAGE ={
        BREADCRUMB_ITEMS : [
            {
                "text": "LMDP",
                "href": "/lmdp/planned_deliveries"
            },
            {
                "text": "Planned Deliveries",
                "href": "#"
            }
        ],
        PAGE_HEADER: "Planned Deliveries",

        SEARCH: {
        SEARCH_HEADER: "Search",
        FILTER_CLUSTER_LABEL: "Cluster",
        FILTER_SITE_LABEL: "Destination Site",
        FILTER_CLUSTER_ERROR_TEXT: "Error while fetching cluster information",
        FILTER_SITE_ERROR_TEXT: "Error while fetching site information",
        SELECT_FILTER_PLACEHOLDER_TEXT: "Choose",
        FILTER_CLUSTER_LOADING_TEXT: "Fetching list of Clusters",
        FILTER_SITES_LOADING_TEXT: "Fetching list of Sites",
        GENERIC_INTERNAL_SERVER_ERROR_MESSAGE: "Internal Server Error",
        ERROR_MESSAGE_DESCRIPTION: "There was a server issue. Refresh the page and try again.",
        MISSING_SEARCH_VALUES_ERROR: "To search, choose a cluster."
        },

        RESULT : {
	
            RESULTS_HEADER: "Deliveries",
            TABLE: {   
                NO_RESULTS_TEXT: "No deliveries found",
                LOADING_TEXT: "Loading search results",
                ASSET_ID_LABEL: "Asset ID",
                DEMAND_ID_LABEL: "Demand ID",
                PO_LOCATION_LABEL: "Position location",
                CABLING_STATUS_LABEL: "Cabling status",
                SHIPMENT_STATUS_LABEL: "Shipment status",
                RTS_TIME_LABEL: "Ready to ship time",
                REGION_LABEL: "Region",  
                CLUSTER_LABEL: "Cluster",  
                AZ_LABEL: "AZ",  
                SITE_LABEL: "Site",
                RACK_TYPE_LABEL: "Rack type",
                RACK_GROUP_LABEL: "Rack group",
                INTERNAL_RACK_TYPE_LABEL: "Internal rack type",
                WEIGHT_LABEL: "Weight",
                UPLINK_CONFIG_LABEL: "Uplink config",
                RATS_CONFIRMATION_LABEL: "Rats confirmation",
                PLANNED_DELIVERY_DATE_LABEL: "Planned delivery date",
                PLANNED_DELIVERY_DATE_SOURCE_LABEL: "Delivery date source",
                PROMISE_DATE: "Promise date",
                ISO_WEEK_LABEL: "ISO Week",
                EXTERNAL_LINKS: "External Links",
                LOOSE_GEAR_DETAILS: "Loose Gear Details",
                AT_LAST_MILE_HUB_LABEL: "At Last Mile Hub",
                EARLIEST_DELIVERY_DATE_LABEL: "Earliest Delivery Date",
                DESTINATION_HUB_ETA_LABEL: "Destination hub ETA",
                DESTINATION_HUB_ETA_SOURCE_LABEL: "Destination hub ETA source",
                CALCULATED_DELIVERY_DATE_SOURCE_LABEL: "Planned Delivery Date Source",
                TARGET_DELIVERY_DATE_LABEL: "Target Delivery Date",
                TARGET_DELIVERY_DATE_STATUS_LABEL: "Target Delivery Date State",
                BLOCKER_REASON_LABEL: "Long Pole for dwelling",
                SUGGESTED_ACTION_LABEL: "Suggested Actions to pull in systematically",
                PFHO_LABEL: "Power Facilities HandOff Date",
                BHO_LABEL: "Brick HandOff Date",
                POSITION_READY_STATUS_LABEL: "Position ready status",
                ROOS_PRIORITY: "ROOS Priority"
            },
            AGGREGATE_VIEW: {
                TOTAL_RACKS: "Total Racks  ",
                SITE_SPREAD: "Site Spread  ",
                UPCOMING_RACKS: "Upcoming Racks",
                EXPANDABLE_SECTION_HEADER: "More Details",
                DAY: "Day",
                LOADER_TEXT: "Aggregate view"
            }
        }
}

export const SEARCH_FILTER = {
    SEARCH_HEADER: "Search",
    EIGHT_WEEK_FORECAST_HEADER: "Choose Cluster",
    FILTER_CLUSTER_LABEL: "Cluster",
    FILTER_SUPER_AZ_LABEL: "Super Az",
    FILTER_POSITION_STATUS_LABEL: "Position Ready Status",
    INTERNAL_RACK_TYPE_LABEL: "Internal Rack Type",
    AT_DEST_HUB_LABEL: "At Destination Hub",
    FILTER_SITE_LABEL: "Destination Site",
    FILTER_HUB_LABEL: "Reached Destination Hub",
    FILTER_REGION_LABEL: "Destination Region",
    FILTER_SHIPMENT_STATUS_LABEL: "Shipment Status",
    FILTER_DESTINATION_HUB_ETA_SOURCE_LABEL: "Destination Hub ETA Source",
    FILTER_RACK_TYPE_LABEL: "Rack Type",
    FILTER_CLUSTER_ERROR_TEXT: "Error while fetching cluster information",
    FILTER_REGION_ERROR_TEXT: "Error while fetching region information",
    FILTER_SHIPMENT_STATUS_ERROR_TEXT: "Error while fetching shipment status information",
    FILTER_DESTINATION_HUB_ETA_SOURCE_ERROR_TEXT: "Error while fetching hub eta source information",
    FILTER_SITE_ERROR_TEXT: "Error while fetching site information",
    FILTER_POSITION_STATUS_ERROR_TEXT: "Error while fetching position status information",
    INTERNAL_RACK_TYPE_ERROR_TEXT: "Error while fetching internal rack type information",
    FILTER_RACK_TYPE_ERROR_TEXT: "Error while fetching rack type information",
    SELECT_FILTER_PLACEHOLDER_TEXT: "Choose",
    FILTER_CLUSTER_LOADING_TEXT: "Fetching list of Clusters",
    FILTER_REGION_LOADING_TEXT: "Fetching list of Regions",
    FILTER_SITES_LOADING_TEXT: "Fetching list of Sites",
    FILTER_SHIPMENT_STATUS_LOADING_TEXT: "Fetching list of Shipment Status",
    FILTER_DESTINATION_HUB_ETA_SOURCE_LOADING_TEXT: "Fetching list of Destination Hub ETA Source",
    FILTER_POSITION_STATUS_LOADING_TEXT: "Fetching list of Position Status",
    INTERNAL_RACK_TYPE_LOADING_TEXT: "Fetching list of Internal Rack Type",
    FILTER_RACK_TYPES_LOADING_TEXT: "Fetching list of Rack Types",
    GENERIC_INTERNAL_SERVER_ERROR_MESSAGE: "Internal Server Error",
    ERROR_MESSAGE_DESCRIPTION: "There was a server issue. Refresh the page and try again.",
    MISSING_SEARCH_VALUES_ERROR: "To search, choose a cluster or superAz.",
    MISSING_SHOW_ALL_RACKS_SEARCH_VALUES_ERROR: "To search, choose a region, cluster or superAz.",
    MISSING_HUB_VIEW_SEARCH_VALUES_ERROR: "To search, choose a region or rack type or site.",
    RACK_TYPE_PLACEHOLDER_TEXT: 'Enter rack types separated with comma or new line',
}

export const LAST_MILE_HUB_VIEW_PAGE = {
    BREADCRUMB_ITEMS : () => [
        {
            "text": "Last Mile Hub View",
            "href": "/lmdp/last_mile_hub_view"

        },
        {
            "text": "Blocked Racks",
            "href": "#"
        }
    ],
    PAGE_HEADER: "Last Mile Hub View",
    GROUP: {
        SITE: "SITE",
        REGION: "REGION",
        INTERNAL_RACK_TYPE: "INTERNAL_RACK_TYPE",
        DELIVERY_CONSTRAINT: "DELIVERY_CONSTRAINT"
    },
    DELIVERY_CONTRAINT: {
        POSITION_BLOCKED: "POSITION",
        THROUGHPUT_BLOCKED: "THROUGHPUT",
        MISSED_TDD: "MISSED_TDD"
    },
    SUMMARY_BUCKETS: {
        TOTAL_COUNT: "TOTAL_COUNT",
        OUTGOING_RACKS: "OUTGOING",
        COUNT_OF_RACKS_APPROACHING_PROMISE: "APPROACHING_PROMISE",
        P90_DWELL_TIME_VALUE: "DWELL_TIME_P90",
        DWELL_TIME_DIMENSIONS_PREFIX: "DWELL_TIME"
    },
    RESULT : {
        RESULTS_HEADER: "Detailed Report",
        TABLE: {   
            NO_RESULTS_TEXT: "No records found",
            LOADING_TEXT: "Loading search results",
            ASSET_ID_LABEL: "Asset ID",
            DEMAND_ID_LABEL: "Demand ID",
            PROMISE_DATE_LABEL: "Promise date",
            BLOCKER_LABEL: "Blocker",
            BLOCKING_REASON_LABEL: "Blocking reason",
            HUB_RECEIVED_AT_LABEL: "Hub received at",
            DWELL_TIME_LABEL: "Dwell time (in days)",
            PO_LOCATION_LABEL: "Position location",
            CABLING_STATUS_LABEL: "Cabling status",  
            CLUSTER_LABEL: "Cluster",  
            AZ_LABEL: "AZ",  
            SITE_LABEL: "Site",
            INTERNAL_RACK_TYPE_LABEL: "Internal rack type",
            RACK_GROUP_LABEL: "Rack group",
            RATS_CONFIRMATION_LABEL: "Rats confirmation",
            PLANNED_DELIVERY_DATE_LABEL: "Planned delivery date",
            PLANNED_DELIVERY_DATE_SOURCE_LABEL: "Delivery date source",
            BRICK_HOSTNAME_LABEL: "Brick",
            BRICK_HANDOFF_DATE_LABEL: "Brick handoff date",
            POSITION_HANDOFF_DATE_LABEL: "Position handoff date",
            POSITION_READY_STATUS_LABEL: "Position ready status"
        }
    }
}

export const SHIPMENT_STATUS_MAPPING : { [key:string]:string; } = {
    'not ready to ship': 'Not Ready to Ship',
    'ready to ship': 'Ready to Ship',
    'shipped': 'Shipped',
    'delivered at dc': 'Delivered at DC',
    'on hold': 'On Hold',
    '-': 'Unknown'
}

export const HUB_ETA_SOURCE_MAPPING : { [key:string]:string; } = {
    'CARRIER': 'Carrier',
    'EDI852': 'Warehouse feed',
    '-': 'System calculated'
}

export const BLOCKER_ACTIONS : { [key:string]:string; } = {
    'AZMAX': 'Constrained AZ capacity for rack receipt in DC facilities',
    'SITEMAX': 'DC site rack capacity limit',
    'SITESPREAD': 'Daily site allocation quota limit breached',
    'HOLIDAY': 'Holidays configured on DC sites on possible TDDs',
    'CABLINGNOTDONE': 'Cabling not completed',
    'NEANOTCOMPLETE': 'Incomplete network early access setup',
    'PFHONOTCOMPLETE': 'Pending power system handover',
    'BHONOTCOMPLETE': 'Pending brick handover',
    'MISSED_TDD': 'Failed to meet target delivery date',
    'POSITIONNOTAVAILABLE': 'Position not available at DC site',
    'RATSUNCONFIRMED': 'RATS confirmation pending',
    'POSITIONDISABLED': 'Position allocated earlier has been disabled',
    'RATSREJECTED': 'Rack allocation has been rejected',
    'BHONOTPRESENT': 'Brick confirmation pending',
};

export const BLOCKED_DELIVERIES_PAGE = {
    BREADCRUMB_ITEMS :() => [
        {
            "text": "Last Mile Delivery Planner",
            "href": "/lmdp/planned_deliveries"
        },
        {
            "text": "Blocked Racks",
            "href": "#"
        }
    ],
    PAGE_HEADER: "Blocked Racks",

    RESULT: {
        THROUGHPUT_BLOCKED_RACK_TABLE: {
            TABLE_HEADER: "Throughput Blocked Deliveries",
            ASSET_ID: "Asset Id",
            COUNT_OF_SKIPPED_TDD: "Count of Skipped TDDs",
            SKIPPED_REASON: "Skipped Reason"
        },
        POSITION_BLOCKED_RACK_TABLE: {
            TABLE_HEADER: "Position Blocked Deliveries",
            ASSET_ID: "Asset Id",
            POSITION_READY_DATE: "Position Ready Date",
            LONG_POLE_REASON: "Long Pole Reason",
            POSITION_STATUS: "Position Status"
        }
    }
}

// Todo refactor common constant at a single place
export const CABLING_MANIFEST_TABLE_CONSTANT ={
    BREADCRUMB_ITEMS : [
        {
            "text": "LMDP",
            "href": "/lmdp/cabling-manifest"
        },
        {
            "text": "Cabling Manifest",
            "href": "#"
        }
    ],
    PAGE_HEADER: "Cabling Manifest",

    SEARCH: {
        SEARCH_HEADER: "Search",
        FILTER_CLUSTER_LABEL: "Cluster",
        FILTER_SITE_LABEL: "Destination Site",
        FILTER_CLUSTER_ERROR_TEXT: "Error while fetching cluster information",
        FILTER_SITE_ERROR_TEXT: "Error while fetching site information",
        SELECT_FILTER_PLACEHOLDER_TEXT: "Choose",
        FILTER_CLUSTER_LOADING_TEXT: "Fetching list of Clusters",
        FILTER_SITES_LOADING_TEXT: "Fetching list of Sites",
        GENERIC_INTERNAL_SERVER_ERROR_MESSAGE: "Internal Server Error",
        ERROR_MESSAGE_DESCRIPTION: "There was a server issue. Refresh the page and try again.",
        MISSING_SEARCH_VALUES_ERROR: "To search, choose a cluster."
    },

    CABLING_MANIFEST_TABLE_CONSTANT : {

        RESULTS_HEADER: "Detailed Report",
        CABLING_MANIFEST_AGGREGATE_TABLE_HEADER: "Upcoming Racks : ",
        TABLE: {
            NO_RESULTS_TEXT: "No Data found",
            LOADING_TEXT: "Loading search results",
            ASSET_ID_LABEL: "Asset ID",
            DEMAND_ID_LABEL: "Demand ID",
            PO_ID_LABEL: "Position ID",
            CABLING_STATUS_LABEL: "Cabling status",
            CLUSTER_LABEL: "Cluster",
            AZ_LABEL: "AZ",
            SITE_LABEL: "Site",
            RACK_TYPE_LABEL: "RackType",
            WEEK: "ISO Week ",
            WEEK_1: "Week 1",
            WEEK_2: "Week 2",
            WEEK_3: "Week 3",
            TOTAL:  "Total"
        },
        ESTIMATED_DELIVERY_DATE_LABEL: "Estimated delivery date",
        TARGET_DELIVERY_DATE_LABEL: "Target delivery date",
        BRICK_HANDOFF_LABEL: "Brick",
        BRICK_HANDOFF_DATE_LABEL: "Brick handoff date",
        POSITION_HANDOFF_DATE_LABEL: "Position handoff date",
        PROMISE_DATE: "Promise date"
    },

    CABLING_MANIFEST_TAB : {
        NOT_CABLED: "Not Cabled",
        CABLING_IN_PROGRESS: "Cabling In Progress",
        CABLING_COMPLETED: "Cabling Completed"
    }

}

export const CABLING_MANIFEST_BAR_CHART_CONSTANT = {
    CHART_HEADER: "TDD assigned assets pending cabling",
    CHART_DESCRIPTION: "This contains all the TDD assigned assets for which cabling has not yet started",
    CHART_ARIALABEL: "Multiple data series line chart",
    Y_AXIS_TITLE: "Number of racks",
    X_AXIS_TITLE: "Target delivery date",
    NO_DATA_TITLE: "No data available",
    NO_DATA_DESCRIPTION: "There is no data available"
}

export const TABLE_COLUMN_HEADER_ICON = {
    UPDATE_SLA : (hoursToUpdate: string) => `This column gets updated every ${hoursToUpdate} hours`
}

export const SUPER_AZ_TO_AZ_MAP = {
    "IAD SOUTH" : ["IAD7", "IAD55", "IAD2", "IAD4", "IAD53", "IAD66", "IAD1", "IAD62"],
    "IAD NORTH" : ["IAD6", "IAD12"],
    "IAD HOLIDAY" : ["IAD187", "IAD188", "IAD189"],
    "IAD ADAMSTOWN" : ["IAD245"],
    "DUB62DUB4" : ["DUB62", "DUB4"],
    "CMH50OSU60" : ["CMH50", "OSU60"],
    "CMH51OSU61" : ["CMH51", "OSU61"],
    "CMH52OSU62" : ["CMH52", "OSU62"],
    "ATL68ATL72" : ["ATL68", "ATL72"],
    "THF" : ["THF60","THF61","THF62"]
}

export const INTERNAL_RACK_GROUP_TO_INTERNAL_RACK_TYPES_MAP = {
    "Series 6/7/8" : ["EC2.C6A.17C", "EC2.C6AD.17C", "EC2.C6AS.17C", "EC2.C6I.19", "EC2.C6ID.19", "EC2.C6IN.19", "EC2.C7A.21",
        "EC2.C7AK.21", "EC2.C7G.21", "EC2.C7GD.21", "EC2.C7GDK.21", "EC2.C7GK.21", "EC2.C7GN.21", "EC2.C7I.21",
        "EC2.C7IK.21", "EC2.C8G.23", "EC2.M6A.17C", "EC2.M6AD.17C", "EC2.M6AS.17C", "EC2.M6I.19", "EC2.M6ID.19",
        "EC2.M6IDN.19", "EC2.M6IN.19", "EC2.M7A.21", "EC2.M7AK.21", "EC2.M7G.21", "EC2.M7GD.21", "EC2.M7GDK.21",
        "EC2.M7GK.21", "EC2.M7I.21", "EC2.M7IK.21", "EC2.M8G.23", "EC2.R6A.17C", "EC2.R6AD.17C", "EC2.R6I.19",
        "EC2.R6ID.19", "EC2.R6IDN.19", "EC2.R6IN.19", "EC2.R7A.21", "EC2.R7AK.21", "EC2.R7G.21", "EC2.R7GD.21",
        "EC2.R7GDK.21", "EC2.R7GK.21", "EC2.R7I.21", "EC2.R7IK.21", "EC2.R8G.23"],
    "S3": ["S3.HANUMANCV1.21", "S3.GARFIELDCV1.16C", "S3.GARFIELDAV1.16C", "S3.HANUMANAV1.21", "MV.BONSAI.24"],
    "GRAVITON 2": ["EC2.C6GN.19", "EC2.I4GEN.19", "EC2.C6GD.19", "EC2.G5G.19", "EC2.X2GD.19", "EC2.M6GD.19", "EC2.R6GD.19", 
        "EC2.C6G.19", "EC2.M6G.19", "EC2.I4G.19", "EC2.R6G.19"],
    "GRAVITON 4": ["EC2.M8GN.23", "EC2.R8GN.23", "EC2.I8GE.23", "EC2.X8G.23", "EC2.M8GC.23", "EC2.R8GC.23", "EC2.M9G.25", 
        "EC2.C8GN.23", "EC2.C8GC.23", "EC2.C8GD.23", "EC2.R8GD.23", "EC2.M8G.23", "EC2.M8GD.23", "EC2.R8G.23", "EC2.C8G.23", 
        "EC2.I8G.23"],
    "INTEL 6-SERIES": ["EC2.C6IDN.19", "EC2.C6I.19", "EC2.R6I.19", "EC2.C6IN.19", "EC2.M6IDN.19", "EC2.M6IN.19", "EC2.R6IN.19", 
        "EC2.R6IDN.19", "EC2.C6ID.19", "EC2.X2D.19", "EC2.M6ID.19", "EC2.HPC6ID.19", "EC2.X2ED.19", "EC2.M6I.19", "EC2.I4.19", 
        "EC2.R6ID.19"],
    "INTEL 7-SERIES": ["EC2.HM732TL.21", "EC2.HM716TL.21", "EC2.HM78TM.21", "EC2.M7I.21", "EC2.HM724TL.21", "EC2.HM7HPE32T.21", 
        "EC2.C7ID.C21CX", "EC2.R7I.21", "EC2.G7E.21", "EC2.C7IK.21", "EC2.HM712TL.21", "EC2.HM76TM.21", "EC2.R7IZ.21", 
        "EC2.R7IK.21", "EC2.I7I.21", "EC2.I7IES.21", "EC2.C7IN.21", "EC2.C7I.21", "EC2.M7IK.21"]
}

//This is temporary Mapping, will be updated once final mapping is created
export const BLOCKER_REASON_TO_SUGGESTED_ACTION_MAP = {
    "User mandated" : "Remove the override",
    "BHO is not done && PFHO is not done" : "Remove position blockers",
    "Throughput blockers" : "Remove throughput blockers"
}

export enum CABLING_STATUS_ENUM {
    CABLED = "CABLED",
    PENDING = "PENDING",
    IN_PROGRESS = "CABLING_IN_PROGRESS",
    DECABLING = "DECABLING",
    NOT_CABLED = "NOT_CABLED"
}

export enum POSITION_STATUS_ENUM {
    RED = "RED",
    YELLOW = "YELLOW",
    GREEN = "GREEN"
}

export const MAX_PLANNED_DELIVERIES_EDIT_ALLOWED: number = 200;

// Make sure to add new entry under reasons in RescheduleDeliveries.smithy in ScosTransportationLMDPModel package
export const RESCHEDULE_REASONS = [
    {value: 'R001', label: 'Rejected by ops on the ground'},
    {value: 'R002', label: 'Ops requested TDD change'},
    {value: 'R003', label: 'Mechanical Breakdown'},
    {value: 'R004', label: 'TMS Error'},
    {value: 'R005', label: 'TMS transit time issue'},
    {value: 'R006', label: 'Clearance Delay'},
    {value: 'R007', label: 'Customs Delay'},
    {value: 'R008', label: 'Customs Hold'},
    {value: 'R009', label: 'Quality issues/Damaged'},
    {value: 'R010', label: 'ODM - Rack not ready/released'},
    {value: 'R011', label: 'ODM - Delayed Pickup'},
    {value: 'R012', label: 'ODM - FCI DELAY'},
    {value: 'R013', label: 'ODM - Quality'},
    {value: 'R014', label: 'ODM - Quarantine Request'},
    {value: 'R015', label: 'ODM - Release'},
    {value: 'R016', label: 'ODM - Document Delay'},
    {value: 'R017', label: 'Redirect - International'},
    {value: 'R018', label: 'Redirect - requires transfer declaration'},
    {value: 'R019', label: 'Systems - Incorrect TDD'},
    {value: 'R020', label: 'LSP planning - Warehouse ISH to ISH'},
    {value: 'R021', label: 'LSP planning - Transportation ISH-ISH'},
    {value: 'R022', label: 'LSP planning - Warehouse ISH to DC'},
    {value: 'R023', label: 'LSP planning - Transportation ISH-DC'},
    {value: 'R024', label: 'Transit delay'},
    {value: 'R025', label: 'Delay due to weather'},
    {value: 'R026', label: 'DC delivery window'},
    {value: 'R027', label: 'Pullin requested by customer'},
    {value: 'R028', label: 'Non-clean slate cluster'},
    {value: 'R101', label: 'EDGE site – planning delivery'},
]

export const RESCHEDULE_REASONS_VALUES_TO_CODE = RESCHEDULE_REASONS.reduce((acc, item) => {
    acc[item.label] = item.value;
    return acc;
}, {} as { [key: string]: string });


export const REQUESTOR_GROUPS = [
    {value: 'DC', label: 'DC'},
    {value: 'ACL', label: 'ACL'},
    {value: 'TPM', label: 'TPM'},
    {value: 'POET', label: 'POET'},
    {value: 'ICM', label: 'ICM'},
    {value: 'ODM', label: 'ODM'},
]
export enum RESCHEDULE_ACTION {
    REJECT = "REJECT",
    OVERRIDE = "OVERRIDE"
}

export const FORBIDDEN_ERROR = 'UnknownError: Forbidden';
export const FORBIDDEN_MESSAGE = 'You do not have access to perform this action.';

export const BANNERS = {
    SUCCESS: {
        id: "SUCCESS",
        type: BANNER_TYPE_SUCCESS,
        header: 'Request submitted successfully for the following assets:',
        loading: false,
        dismissible: true
    },
    IN_PROGRESS: {
        id: "IN_PROGRESS",
        type: BANNER_TYPE_SUCCESS,
        header: 'Your request is being processed.',
        loading: true,
        dismissible: false
    },
    WARNING: {
        type: BANNER_TYPE_WARNING,
        loading: false,
        dismissible: true
    },
    VALIDATION_ERROR: {
        type: BANNER_TYPE_ERROR,
        loading: false,
        dismissible: true
    },
    UNKNOWN_ERROR: {
        id: "UNKNOWN_ERROR",
        type: BANNER_TYPE_ERROR,
        header: 'There was an error submitting your request. Please refresh the page or try again later.',
        loading: false,
        dismissible: true
    }
}

export const RESCHEDULE_ERROR_CODES = {
    REQUEST_EXISTS: {
        id: 'REQUEST_EXISTS',
        bannerItem: BANNERS.WARNING,
        header: 'No action needed. An ongoing request already exists for the following assets:'},
    DUPLICATE_REQUEST: {
        id: 'DUPLICATE_REQUEST',
        bannerItem: BANNERS.VALIDATION_ERROR,
        header: 'The following assets occurs multiple times in the request:'},
    INVALID_ASSET: {
        id: 'INVALID_ASSET',
        bannerItem: BANNERS.VALIDATION_ERROR,
        header: 'The following assets do not meet the requirements in current Shipment Plan file:'},
    TDD_NOT_IN_VALID_RANGE: {
        id: 'TDD_NOT_IN_VALID_RANGE',
        bannerItem: BANNERS.VALIDATION_ERROR,
        header: 'Target Delivery Date needs to be within two years from now:'}
}
